import {
    BaseLayer,
    BaseLayerFactory_3857,
    BaseLayerType
} from "esdm-map";

import { IBaseLayer } from "esdm-map/dist/baselayers/interfaces/IBaseLayer";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS';

import * as olProj from 'ol/proj';
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import Projection from "ol/proj/Projection";
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import ImageLayer from "ol/layer/Image";
import ImageWMS from 'ol/source/ImageWMS';
import WMTS from 'ol/source/WMTS'; 
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import { get as getProjection } from "ol/proj";
import { getWidth } from "ol/extent";
import TileGrid from "ol/tilegrid/TileGrid"

export const AberdeenshireBaseLayerTypes = {
    ...BaseLayerType,
    AberdeenshireAerial: 996,
    AberdeenshireOSHistoric_1stEdition: 997,
    AberdeenshireOSHistoric_2ndEdition: 998,
    AberdeenshireOS: 999
};

export class AberdeenshireBaseMapping_3857 extends BaseLayerFactory_3857 {

    public GetBaseLayerByType(baseLayerType: BaseLayerType, bingApiKey: string, esdmOsmApiKey:string): IBaseLayer {
        if (baseLayerType === AberdeenshireBaseLayerTypes.AberdeenshireOS) {
            return this.GetAberdeenshireOS();
        }

        if (baseLayerType === AberdeenshireBaseLayerTypes.AberdeenshireAerial) {
            return this.GetAberdeenshireAerial();
        }

        if (baseLayerType === AberdeenshireBaseLayerTypes.AberdeenshireOSHistoric_2ndEdition) {
            return this.GetNLSHistoric_2ndEdition();
        }

        if (baseLayerType === AberdeenshireBaseLayerTypes.AberdeenshireOSHistoric_1stEdition) {
            return this.GetNLSHistoric_1stEdition();
        }
        
        return super.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
    }   

    private GetAberdeenshireOS(): IBaseLayer {
        
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                //Configured via OGC Proxy
                url: "/api/OGCService/AberdeenshireOS",
                layer: "Road_3857",
                matrixSet: "EPSG:3857",
                format: "image/png",
                projection: "EPSG:3857",
                tileGrid: new WMTSTileGrid({
                    origin: [-20037508.342787, 20037508.342787],
                    resolutions: [1222.9924523698928, 611.4962261850814, 305.7481130924087, 152.87405654633633, 76.43702827303586, 38.21851413651793, 19.109257068258966, 9.554628534129483, 4.777314267197037, 2.3886571335985134, 1.1943285666669667, 0.5971642834657753, 0.2985821416005962, 0.1492910708002979],
                    matrixIds: ["EPSG:3857:7", "EPSG:3857:8", "EPSG:3857:9", "EPSG:3857:10", "EPSG:3857:11", "EPSG:3857:12", "EPSG:3857:13", "EPSG:3857:14", "EPSG:3857:15", "EPSG:3857:16", "EPSG:3857:17", "EPSG:3857:18", "EPSG:3857:19", "EPSG:3857:20"]
                }),
                style: "default",
                crossOrigin: "Anonymous",
                attributions: [`© Crown copyright and database rights ${new Date().getFullYear()} OS AC0000851922.`,
                    'Use of this data is subject to <a href="/map-terms-and-conditions#os">terms and conditions</a>.']
            })
        });
        return new BaseLayer("AberdeenshireOS", "Ordnance Survey", lyr);
    } 

    private GetAberdeenshireAerial(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/AberdeenshireAerial",
                params: {
                    LAYERS: 'APGB_Latest_UK_125mm',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:3857'
                },
                projection: "EPSG:3857",
                crossOrigin: "Anonymous",
                attributions: ['© Bluesky International Limited and Getmapping Limited 1999 - 2023',
                    '© Bluesky International Limited 2024 and onwards.',
                    'Use of this data is subject to <a href="/map-terms-and-conditions#apgb">terms and conditions</a>']
            })
        });
        return new BaseLayer("AberdeenshireAerial", "Aerial Photography", lyr);
    }

    protected GetNLSHistoric_1stEdition(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new XYZ({
                url: "/api/OGCService/NLSLayers/os/6inchfirst/{z}/{x}/{y}.png",
                projection: "EPSG:3857",
                attributions: ['Mapping from <a href="https://www.nls.uk/">National Library of Scotland</a> <img class="maptiler-logo" src="/images/maptiler-logo-adaptive.svg" alt="MapTiler logo" />'],
                crossOrigin: 'anonymous'
            })
        });
        return new BaseLayer("NLSHistoric1stEdition", "Ordnance Survey 6 inch (1843-1888)", lyr);
    }

    protected GetNLSHistoric_2ndEdition(): IBaseLayer {
        const lyr = new TileLayer({
            extent: olProj.transformExtent([-7.68, 49.8, 1.77, 60.4], 'EPSG:4326', 'EPSG:3857'),
            preload: Infinity,
            source: new XYZ({
                urls: [
                    'https://api.maptiler.com/tiles/uk-osgb10k1888/{z}/{x}/{y}.jpg?key=h4V2flgXdHNV9pOmyIU2'
                ],
                minZoom: 1,
                maxZoom: 18,
                attributions: 'Mapping from <a href="https://www.nls.uk/">National Library of Scotland</a> <img class="maptiler-logo" src="/images/maptiler-logo-adaptive.svg" alt="MapTiler logo" />',
            }),
            visible: false,
        });
        lyr.set("name", "OSHistoric_2ndEdition", true);
        return new BaseLayer("AberdeenshireOSHistoric_2ndEdition", "Ordnance Survey 6 inch (1888-1913)", lyr);
    }
}